// WORKSPACE CALLS — the shell call panel's runtime (comms-phone v2, "calls
// inside the workspace"). Nika 2026-10-01: "we should be making all calls and
// messaging preferebly inside the workspace through twillio/whatsapp APIs".
//
// ONE voice session per workspace session. The SDK owns every wire detail:
// `sdk.getService('channels').voiceClient({ workspaceId })` mints the voice
// token, opens the per-member call stream, and — for a LIVE Twilio number
// only — lazy-loads the Twilio Voice JS SDK with its own dynamic import
// (FA206). This file never imports Twilio and never opens a socket or a
// fetch: it drives the panel state from the voice client's events.
//
// THE PANEL STATE is ONE root key, `_callPanel` (null = idle, panel hidden),
// always written in its FULL shape (PANEL_SHAPE) so a merge never leaves a
// stale field from the previous call. `_callPanelTick` is a 1 s clock nudge
// that runs only while a call is answered (the in-call timer).
//
// CAPABILITY: the panel stays hidden when the SDK has no
// `channels.voiceClient` (an SDK build before comms-phone v2), when the
// server refuses a voice session (403 forbidden_workspace_role, 409
// no_browser_number / browser_calling_not_available, 400 account_required),
// or when the member is signed out. The server is the role gate: an owner,
// an admin or a member (stored `editor`) gets a token; a Contributor (stored
// `viewer`) or a guest gets the 403, which is KEPT, never retried on a clock
// (THE ROLE GATE below).
//
// THE LIVE OBJECTS (the voice client, the call handle, the pending incoming
// call) are not data and cannot ride state; they live in `_rt` below — the
// same module-singleton shape meet's connectCall.js uses for its LiveKit Room,
// for the same reason (a route change must never tear a call down). The
// workspace switch (functions/loadPrivateData.js) and the shell's onRemove
// call stopWorkspaceVoice, so a call never follows the member into another
// tenant.

// ── pure model ────────────────────────────────────────────────────────────
export const CALL_TERMINAL = ['completed', 'no_answer', 'busy', 'failed', 'canceled']

// How long an ended call stays on screen before the panel hides itself. A
// MISSED call stays until the member dismisses it (the bell carries it too).
export const CALL_ENDED_LINGER_MS = 8000

export const PANEL_SHAPE = Object.freeze({
  callId: null,
  direction: 'out',
  status: 'initiated',
  phase: 'outgoing',
  name: null,
  number: null,
  answeredAt: null,
  durationSec: null,
  muted: false,
  held: false,
  keypadOpen: false,
  digits: '',
  confirmEnd: false,
  canMute: false,
  canHold: false,
  canKeypad: false,
  busy: false,
  errorKey: null
})

// The literal key per ended state — never built by concatenation (the i18n
// gate resolves every key from its literal).
const OUT_ENDED_KEYS = {
  completed: 'workspace.callPanel.ended.completed',
  no_answer: 'workspace.callPanel.ended.noAnswer',
  busy: 'workspace.callPanel.ended.busy',
  failed: 'workspace.callPanel.ended.failed',
  canceled: 'workspace.callPanel.ended.canceled'
}

export const normalizeCallDirection = (value) =>
  typeof value === 'string' && /^in/i.test(value) ? 'in' : 'out'

export const callPhaseFor = (status, direction) => {
  if (CALL_TERMINAL.includes(status)) return 'ended'
  if (status === 'answered') return 'active'
  return direction === 'in' ? 'incoming' : 'outgoing'
}

export const isMissedCall = (panel) =>
  !!panel && panel.phase === 'ended' && panel.direction === 'in' && panel.status !== 'completed'

export const callEndedKey = (panel) => {
  if (!panel) return OUT_ENDED_KEYS.failed
  if (isMissedCall(panel)) return 'workspace.callPanel.ended.missed'
  return OUT_ENDED_KEYS[panel.status] || OUT_ENDED_KEYS.failed
}

// The eyebrow line above the name, per phase.
export const callEyebrowKey = (panel) => {
  if (!panel) return null
  if (panel.phase === 'incoming') return 'workspace.callPanel.incoming'
  if (panel.phase === 'active')
    return panel.held ? 'workspace.callPanel.onHold' : 'workspace.callPanel.onCall'
  if (panel.phase === 'ended') return callEndedKey(panel)
  return panel.status === 'ringing' ? 'workspace.callPanel.ringing' : 'workspace.callPanel.calling'
}

export const formatCallDuration = (seconds) => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = String(total % 60).padStart(2, '0')
  return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`
}

export const callElapsedSeconds = (panel, now = Date.now()) => {
  if (!panel) return 0
  if (panel.phase === 'ended' && panel.durationSec != null) return panel.durationSec
  const at = panel.answeredAt ? Date.parse(panel.answeredAt) : NaN
  return Number.isFinite(at) ? Math.max(0, Math.floor((now - at) / 1000)) : 0
}

// A call row (the timeline's call MESSAGE) → the panel's fields. `base` carries
// what the row may not (the name the caller passed, the in-call flags).
export const callPanelFromRow = (row, base = {}) => {
  const call = (row && row.call) || {}
  const direction = normalizeCallDirection(
    call.direction || (row && row.direction) || base.direction
  )
  const status = call.status || base.status || PANEL_SHAPE.status
  const number = row ? (direction === 'in' ? row.from : row.to) : null
  const party = row && row.party
  return {
    ...PANEL_SHAPE,
    ...base,
    callId: (row && row.id) || base.callId || null,
    direction,
    status,
    phase: callPhaseFor(status, direction),
    name: (party && party.name) || base.name || null,
    number: number || base.number || null,
    answeredAt: call.answeredAt || base.answeredAt || null,
    durationSec:
      call.durationSec != null
        ? call.durationSec
        : base.durationSec != null
          ? base.durationSec
          : null
  }
}

// A ring moves focus into the panel only when the member is not typing: an
// input that takes text, a textarea, a select or a contenteditable keeps it
// (design-d2 review). Buttons, checkboxes and the page body give it up.
const NON_TEXT_INPUTS = [
  'button',
  'checkbox',
  'radio',
  'submit',
  'reset',
  'range',
  'color',
  'file',
  'image'
]
export const ringMayTakeFocus = (active) => {
  if (!active) return true
  if (active.isContentEditable) return false
  const tag = String(active.tagName || '').toUpperCase()
  if (tag === 'TEXTAREA' || tag === 'SELECT') return false
  if (tag === 'INPUT') return NON_TEXT_INPUTS.includes(String(active.type || 'text').toLowerCase())
  return true
}

// `--call-clear`: what a bottom-anchored toast must clear while the panel is
// up (design-d2 review: at 360 x 740 a toast covered Accept / Decline).
// The shell's toast stack docks on --stack-line and holds var(--call-clear,
// 0%) under its toasts (app.js GlobalNotification), and the card itself sits
// on that stack line, so the value is the card's height plus the gap between
// the card and the toast (Z1, the panel's own inner gap). null = hidden: the
// property is removed and the toast falls back to 0%.
export const callClearFor = (rect) => {
  if (!rect || !rect.height) return null
  return `calc(${Math.ceil(rect.height)}px + var(--spacing-Z1))`
}

// The thin capability check: an SDK without the comms-phone v2 voice client
// keeps the panel hidden.
export const workspaceCallCapable = (channels) =>
  !!channels && typeof channels.voiceClient === 'function'

// The refusal code. comms-phone v3 (7): every channels SDK refusal carries
// `err.code` — the server's machine code, else `http_<status>` — and the voice
// client's own errors use the same field. Nothing else is read.
export const voiceErrorCode = (err) =>
  err && err.code != null && err.code !== '' ? String(err.code) : null

// Refusals that will not change by retrying soon: no voice number, several
// numbers and no default, a 403 with no machine code. Retried after
// REFUSED_RETRY_MS (an admin may connect a number meanwhile); anything else
// after TRANSIENT_RETRY_MS. The role refusal is not on a clock (THE ROLE GATE).
const REFUSED_CODES = [
  'http_403',
  'forbidden',
  'several_twilio_accounts',
  'no_browser_number',
  'browser_calling_not_available',
  'account_required',
  'account_paused',
  'account_disconnected'
]
const REFUSED_RETRY_MS = 10 * 60 * 1000
const TRANSIENT_RETRY_MS = 60 * 1000

// THE ROLE GATE. The server answers a voice session, a call start, an answer
// and a hang-up for an owner, an admin or a member (the channels routes'
// requireWorkspaceEditor); a Contributor (stored `viewer`) or a guest gets
// 403 `forbidden_workspace_role`. That refusal is KEPT for the workspace: a
// role does not change by waiting, and a clock retry is one more 403 on a
// route sync. It lifts when the member's role read (the server's own answer
// on the workspace read, roleHelpers.js callerWorkspaceRole) moves to ANOTHER
// known role, on a workspace switch and on sign-out (stopWorkspaceVoice). No
// role list lives here: the server stays the gate.
const _callRoleRefused = (code) => code === 'forbidden_workspace_role'

// ── runtime ───────────────────────────────────────────────────────────────
const _rt = {
  voice: null,
  channels: null,
  session: null,
  wsId: null,
  starting: null,
  root: null,
  el: null,
  panel: null,
  handle: null,
  handleOff: null,
  incoming: null,
  tick: null,
  linger: null,
  blockedWs: null,
  retryAt: 0,
  refusal: null,
  // The role the member had when the server refused it (THE ROLE GATE); null
  // while the role read is not known yet.
  refusedRole: null,
  unsupported: false
}

const _shellWsIdOf = (root) => {
  if (!root) return null
  const ws = root.workspace
  return (ws && (ws.id || ws._id)) || root.activeWorkspaceId || null
}

const _clearTimers = () => {
  if (_rt.tick) clearInterval(_rt.tick)
  if (_rt.linger) clearTimeout(_rt.linger)
  _rt.tick = null
  _rt.linger = null
}

const _write = (panel) => {
  _rt.panel = panel ? { ...PANEL_SHAPE, ...panel } : null
  const root = _rt.root
  if (root && typeof root.update === 'function') {
    root.update({ _callPanel: _rt.panel ? { ..._rt.panel } : null }, { preventFetch: true })
  }
  const phase = _rt.panel && _rt.panel.phase
  if (phase === 'active' && !_rt.tick) {
    _rt.tick = setInterval(() => {
      if (_rt.root && typeof _rt.root.update === 'function') {
        _rt.root.update({ _callPanelTick: Date.now() }, { preventFetch: true })
      }
    }, 1000)
  } else if (phase !== 'active' && _rt.tick) {
    clearInterval(_rt.tick)
    _rt.tick = null
  }
  if (_rt.linger && phase !== 'ended') {
    clearTimeout(_rt.linger)
    _rt.linger = null
  }
}

const _shellPatch = (partial) => {
  if (!_rt.panel) return
  _write({ ..._rt.panel, ...partial })
}

const _detachHandle = () => {
  if (typeof _rt.handleOff === 'function') {
    try {
      _rt.handleOff()
    } catch (_) {
      /* already detached */
    }
  }
  _rt.handle = null
  _rt.handleOff = null
}

// A call reached a terminal state: drop the live objects, keep the summary.
const _shellSettle = () => {
  _detachHandle()
  _rt.incoming = null
  const panel = _rt.panel
  if (!panel || panel.phase !== 'ended' || isMissedCall(panel)) return
  if (_rt.linger) clearTimeout(_rt.linger)
  const callId = panel.callId
  _rt.linger = setTimeout(() => {
    _rt.linger = null
    if (_rt.panel && _rt.panel.callId === callId && _rt.panel.phase === 'ended') _write(null)
  }, CALL_ENDED_LINGER_MS)
}

// Apply a fresh call row (stream frame, handle state, getCall answer).
const _applyRow = (row, extra = {}) => {
  const panel = _rt.panel
  if (!panel || !row || (row.id && panel.callId && row.id !== panel.callId)) return
  // ONE end per call: once ended, a later frame (the stream confirming a
  // local end, a refined status) changes nothing but a duration the server
  // measured. No second write, no second settle.
  if (panel.phase === 'ended') {
    const sec = row.call && row.call.durationSec
    if (typeof sec === 'number' && sec !== panel.durationSec) _write({ ...panel, durationSec: sec })
    return
  }
  const next = callPanelFromRow(row, { ...panel, ...extra })
  // The panel's own flags survive a row update; a terminal row clears them.
  if (next.phase === 'ended') {
    next.confirmEnd = false
    next.keypadOpen = false
    next.busy = false
    if (next.durationSec == null && panel.answeredAt) next.durationSec = callElapsedSeconds(panel)
  }
  _write(next)
  if (next.phase === 'ended') _shellSettle()
}

const _attachHandle = (handle) => {
  _detachHandle()
  if (!handle) return
  _rt.handle = handle
  const offs = []
  if (typeof handle.on === 'function') {
    offs.push(
      handle.on('state', () => {
        if (_rt.handle === handle) _applyRow(handle.call)
      }),
      // v3 (1): the handle's hold state is the truth (simulated always; live
      // only when the server reports capabilities.hold).
      handle.on('hold', (on) => {
        if (_rt.handle === handle) _shellPatch({ held: !!on })
      })
    )
  }
  _rt.handleOff = () => offs.forEach((off) => typeof off === 'function' && off())
  _shellPatch({
    held: typeof handle.isHeld === 'function' ? !!handle.isHeld() : false,
    canMute: typeof handle.mute === 'function',
    canHold: typeof handle.hold === 'function',
    canKeypad: typeof handle.sendDigits === 'function',
    muted: typeof handle.isMuted === 'function' ? !!handle.isMuted() : false
  })
}

const _refreshPanelCall = () => {
  const panel = _rt.panel
  const channels = _rt.channels
  if (!panel || !panel.callId || !channels || typeof channels.getCall !== 'function') return
  const callId = panel.callId
  Promise.resolve(channels.getCall(callId, { workspaceId: _rt.wsId }))
    .then((res) => {
      const row = (res && res.call && res.call.id ? res.call : null) || (res && res.id ? res : null)
      if (row && _rt.panel && _rt.panel.callId === callId) _applyRow(row)
    })
    .catch(() => {
      /* the next stream frame reconciles */
    })
}

// A Device or call error during a call (voice_device_error, call_error): the
// panel names it; the call's own state still comes from its frames. A role
// refusal with no call on screen (the token renewal of a member whose role
// dropped) ends the session the server no longer honours.
const _onError = (voice) => (err) => {
  if (_rt.voice !== voice) return
  const code = voiceErrorCode(err)
  const onCall = !!_rt.panel && _rt.panel.phase !== 'ended'
  if (_callRoleRefused(code) && !onCall) {
    _callEndForRole(_rt.el, code)
    return
  }
  if (!onCall) return
  _shellPatch({ errorKey: callErrorKey(code) })
}

const _onIncoming = (voice) => (incoming) => {
  if (_rt.voice !== voice || !incoming) return
  // Already on a call: let this one ring through to the member's next leg.
  if (_rt.panel && _rt.panel.phase !== 'ended') return
  _clearTimers()
  _detachHandle()
  _rt.incoming = incoming
  // v3: the caller hung up before accept → the incoming moves to 'canceled'
  // at once (SDK d742a1a). End the ring now; the local frame and the stream
  // frame that follow are no-ops (one end).
  if (typeof incoming.on === 'function') {
    incoming.on('state', (status) => {
      if (_rt.incoming !== incoming || !CALL_TERMINAL.includes(status)) return
      _applyRow({
        ...(incoming.call || { id: incoming.callId }),
        id: incoming.callId || (incoming.call && incoming.call.id),
        call: { ...((incoming.call && incoming.call.call) || {}), direction: 'in', status }
      })
    })
  }
  _write(
    callPanelFromRow(incoming.call || null, {
      callId: incoming.callId || null,
      direction: 'in',
      status: 'ringing'
    })
  )
  // Live mode hands over no row (the Twilio call carries only the id): read it
  // for the caller's name and number.
  if (!incoming.call) _refreshPanelCall()
}

const _onFrame = (voice) => (frame) => {
  if (_rt.voice !== voice || !frame || !_rt.panel) return
  if (frame.id && frame.id === _rt.panel.callId && frame.call) _applyRow(frame.call)
}

const _onOpen = (voice) => () => {
  if (_rt.voice === voice) _refreshPanelCall()
}

const _callTeardown = () => {
  const voice = _rt.voice
  _clearTimers()
  _detachHandle()
  _rt.incoming = null
  _rt.voice = null
  _rt.session = null
  _rt.channels = null
  _rt.wsId = null
  _rt.starting = null
  if (voice && typeof voice.stop === 'function') {
    try {
      voice.stop()
    } catch (_) {
      /* already stopped */
    }
  }
}

// THE ROLE GATE helpers. The role read is the shell's own (the server's answer
// on the workspace read); undefined while it has not landed.
const _callRoleOf = (el) => {
  if (!el || typeof el.call !== 'function') return null
  const role = el.call('callerWorkspaceRole', _rt.root || (el.state && el.state.root))
  return typeof role === 'string' && role ? role : null
}

// Keep a role refusal for this workspace, with the role the member had when
// the server said no.
const _callKeepRoleRefusal = (wsId, code, role) => {
  _rt.refusal = code
  _rt.blockedWs = wsId
  _rt.retryAt = Infinity
  _rt.refusedRole = role || null
}

// A kept role refusal lifts only when the role read moves to ANOTHER known
// role (a promotion reaches the shell on its next workspace read). Unknown to
// known is no change: the server already judged the role the member has.
const _callRoleRecheck = (el) => {
  const role = _callRoleOf(el)
  if (!role) return
  if (!_rt.refusedRole) {
    _rt.refusedRole = role
    return
  }
  if (role === _rt.refusedRole) return
  _rt.blockedWs = null
  _rt.retryAt = 0
  _rt.refusal = null
  _rt.refusedRole = null
}

// The server refused this member's role on a live session (the role dropped
// mid-session): end the session it no longer honours, so this tab rings no
// more, and keep the refusal. An ended summary on screen keeps its own timer.
const _callEndForRole = (el, code) => {
  const wsId = _rt.wsId
  _callTeardown()
  if (wsId) _callKeepRoleRefusal(wsId, code, _callRoleOf(el))
  _shellSettle()
}

// The line for a refused in-call action: the role line when the server
// refused the member's role, else the action's own line. The call stays on
// screen with its hang-up: the server still holds it.
const _callRefusalLine = (err, line) => {
  const code = voiceErrorCode(err)
  return _callRoleRefused(code) ? callErrorKey(code) : line
}

const _channelsOf = async (el) => {
  const sdk = el && typeof el.call === 'function' ? el.call('getSDK') : null
  if (!sdk || typeof sdk.getService !== 'function') return null
  return Promise.resolve(sdk.getService('channels')).catch(() => null)
}

// ── registered functions (el.call) ────────────────────────────────────────

// Idempotent, workspace-keyed: the shell calls it on boot and on every route
// sync (app.js _ensureShellRealtime). A workspace change tears the old session
// down first.
export async function ensureWorkspaceVoice() {
  const root = this && this.state && this.state.root
  if (!root) return null
  // Signed out (logout resets the root and routes to /login without a shell
  // remount): end any session that is still up.
  if (!(root.signedIn || root.currentUserEmail)) {
    if (_rt.voice || _rt.panel || _rt.starting) stopWorkspaceVoice.call(this)
    return null
  }
  const wsId = _shellWsIdOf(root)
  if (!wsId) return null
  if (_rt.wsId && _rt.wsId !== wsId) {
    _callTeardown()
    _write(null)
  }
  _rt.root = root
  _rt.el = this
  if (_rt.voice && _rt.wsId === wsId) return _rt.session
  if (_rt.starting) return _rt.starting
  if (_rt.unsupported) return null
  if (_rt.blockedWs === wsId && _callRoleRefused(_rt.refusal)) _callRoleRecheck(this)
  if (_rt.blockedWs === wsId && Date.now() < _rt.retryAt) return null
  _rt.wsId = wsId
  const el = this
  const run = (async () => {
    const channels = await _channelsOf(el)
    if (_rt.wsId !== wsId) return null
    if (!workspaceCallCapable(channels)) {
      _rt.unsupported = !!channels
      _rt.blockedWs = wsId
      _rt.retryAt = Date.now() + TRANSIENT_RETRY_MS
      _rt.wsId = null
      return null
    }
    const voice = channels.voiceClient({ workspaceId: wsId })
    try {
      voice.on('incoming', _onIncoming(voice))
      voice.on('call', _onFrame(voice))
      voice.on('open', _onOpen(voice))
      voice.on('error', _onError(voice))
      const session = await voice.start()
      if (_rt.wsId !== wsId) {
        voice.stop()
        return null
      }
      _rt.voice = voice
      _rt.channels = channels
      _rt.session = session || null
      _rt.blockedWs = null
      _rt.refusal = null
      _rt.refusedRole = null
      return _rt.session
    } catch (err) {
      try {
        voice.stop()
      } catch (_) {
        /* never started */
      }
      const code = voiceErrorCode(err)
      if (_callRoleRefused(code)) _callKeepRoleRefusal(wsId, code, _callRoleOf(el))
      else {
        _rt.refusal = code
        _rt.blockedWs = wsId
        _rt.retryAt =
          Date.now() + (REFUSED_CODES.includes(code) ? REFUSED_RETRY_MS : TRANSIENT_RETRY_MS)
      }
      if (_rt.wsId === wsId) _rt.wsId = null
      return null
    }
  })()
  _rt.starting = run
  run.finally(() => {
    if (_rt.starting === run) _rt.starting = null
  })
  return run
}

// Sign-out, workspace switch, shell unmount. Idempotent.
export function stopWorkspaceVoice() {
  const root = (this && this.state && this.state.root) || _rt.root
  _callTeardown()
  _rt.blockedWs = null
  _rt.retryAt = 0
  _rt.refusedRole = null
  if (root) _rt.root = root
  _write(null)
  _rt.root = null
  _rt.el = null
}

export async function acceptWorkspaceCall() {
  const incoming = _rt.incoming
  if (!incoming || !_rt.panel || _rt.panel.busy) return false
  _shellPatch({ busy: true, confirmEnd: false, errorKey: null })
  try {
    const handle = await incoming.accept()
    if (_rt.incoming !== incoming) return false
    _rt.incoming = null
    _attachHandle(handle)
    _shellPatch({ busy: false })
    if (handle && handle.call) _applyRow(handle.call)
    return true
  } catch (err) {
    const code = voiceErrorCode(err)
    if (_callRoleRefused(code)) {
      // The server refused the answer for this member's role (the role
      // dropped mid-session): the ring ends here as a missed call with the
      // role line, and the session ends, so no Accept is left to press.
      const panel = _rt.panel
      _callEndForRole(this, code)
      if (panel) {
        _write({
          ...panel,
          status: panel.phase === 'ended' ? panel.status : 'no_answer',
          phase: 'ended',
          busy: false,
          confirmEnd: false,
          keypadOpen: false,
          errorKey: callErrorKey(code)
        })
      }
      return false
    }
    _shellPatch({ busy: false, errorKey: 'workspace.callPanel.error.accept' })
    return false
  }
}

export async function declineWorkspaceCall() {
  const incoming = _rt.incoming
  if (!incoming) return false
  _rt.incoming = null
  // A declined call leaves no summary: the member chose not to take it, and
  // the server rings the next leg (or records the miss and rings the bell).
  _write(null)
  try {
    await incoming.reject()
    return true
  } catch (_) {
    return false
  }
}

export async function hangUpWorkspaceCall() {
  if (_rt.incoming && !_rt.handle) return declineWorkspaceCall.call(this)
  const handle = _rt.handle
  if (!_rt.panel || _rt.panel.phase === 'ended') return false
  _shellPatch({ busy: true, confirmEnd: false, keypadOpen: false, errorKey: null })
  try {
    if (!handle) {
      _shellPatch({ busy: false, errorKey: 'workspace.callPanel.error.hangUp' })
      return false
    }
    await handle.hangup()
    // The stream frame settles the panel; a handle that already knows the end
    // settles it now.
    // v3 (6): hangup() settles the handle at once; the stream frame confirms.
    if (CALL_TERMINAL.includes(handle.status)) _applyRow(handle.call)
    else _shellPatch({ busy: false })
    return true
  } catch (err) {
    _shellPatch({
      busy: false,
      errorKey: _callRefusalLine(err, 'workspace.callPanel.error.hangUp')
    })
    return false
  }
}

export function toggleWorkspaceCallMute() {
  const handle = _rt.handle
  if (!handle || typeof handle.mute !== 'function') return false
  const next = !(typeof handle.isMuted === 'function'
    ? handle.isMuted()
    : _rt.panel && _rt.panel.muted)
  handle.mute(next)
  _shellPatch({ muted: typeof handle.isMuted === 'function' ? !!handle.isMuted() : next })
  return true
}

export async function toggleWorkspaceCallHold() {
  const handle = _rt.handle
  if (!handle || typeof handle.hold !== 'function' || !_rt.panel) return false
  const next = !_rt.panel.held
  try {
    await handle.hold(next)
    _shellPatch({ held: typeof handle.isHeld === 'function' ? !!handle.isHeld() : next })
    return true
  } catch (err) {
    _shellPatch({ errorKey: _callRefusalLine(err, 'workspace.callPanel.error.hold') })
    return false
  }
}

export function toggleWorkspaceCallKeypad() {
  if (!_rt.panel || !_rt.panel.canKeypad) return false
  _shellPatch({ keypadOpen: !_rt.panel.keypadOpen })
  return true
}

export function pressWorkspaceCallDigit(digit) {
  const handle = _rt.handle
  const d = String(digit == null ? '' : digit)
  if (!handle || typeof handle.sendDigits !== 'function' || !/^[0-9*#]$/.test(d)) return false
  try {
    handle.sendDigits(d)
  } catch (_) {
    // v3 (1): the handle refuses what it cannot send (invalid_digits).
    return false
  }
  _shellPatch({ digits: `${(_rt.panel && _rt.panel.digits) || ''}${d}`.slice(-24) })
  return true
}

// Escape: the first press asks, the second press ends (or declines) the call.
// On an ended call it dismisses the summary.
export async function requestEndWorkspaceCall() {
  const panel = _rt.panel
  if (!panel) return false
  if (panel.phase === 'ended') return dismissWorkspaceCall.call(this)
  if (!panel.confirmEnd) {
    _shellPatch({ confirmEnd: true, keypadOpen: false })
    return true
  }
  return panel.phase === 'incoming'
    ? declineWorkspaceCall.call(this)
    : hangUpWorkspaceCall.call(this)
}

export function cancelEndWorkspaceCall() {
  if (!_rt.panel || !_rt.panel.confirmEnd) return false
  _shellPatch({ confirmEnd: false })
  return true
}

export function dismissWorkspaceCall() {
  if (!_rt.panel || _rt.panel.phase !== 'ended') return false
  _write(null)
  return true
}

// ── starting a call (slice 2: the entry point other surfaces el.call) ────────

// Refusals that mean "this workspace cannot call from the browser": the
// caller falls back (the device path, tel:), and the panel never shows.
const NO_BROWSER_CODES = [
  'several_twilio_accounts',
  'no_browser_number',
  'browser_calling_not_available',
  'account_required',
  'account_paused',
  'account_disconnected',
  'http_403',
  'forbidden'
]

// The voice client names every error (SDK d742a1a): err.code only, the
// original on err.cause. A microphone refusal of any origin (Twilio 31208 /
// 31401 / 31402, a browser NotAllowedError) is `microphone_denied`.
export const isMicrophoneRefusal = (err) => voiceErrorCode(err) === 'microphone_denied'

// The panel line per voice-client code — literal keys (the i18n gate reads
// them); an unknown code takes the generic "did not start" line.
const CALL_ERROR_KEYS = {
  microphone_denied: 'workspace.callPanel.error.microphone',
  call_not_connected: 'workspace.callPanel.error.notConnected',
  voice_device_unavailable: 'workspace.callPanel.error.device',
  voice_device_error: 'workspace.callPanel.error.device',
  voice_sdk_unavailable: 'workspace.callPanel.error.device',
  call_error: 'workspace.callPanel.error.callError',
  call_not_started: 'workspace.callPanel.error.start',
  forbidden_workspace_role: 'workspace.callPanel.error.role'
}
export const callErrorKey = (code) =>
  (code && CALL_ERROR_KEYS[code]) || 'workspace.callPanel.error.start'

// Why there is no session: no voice client in the SDK, the server's refusal
// code (v3: e.g. several_twilio_accounts), else 'unavailable'.
const _refusalCode = () => (_rt.unsupported ? 'unsupported' : _rt.refusal || 'unavailable')

// Can this member call from the workspace right now? For the surfaces that
// offer "Call from the workspace" (comms-ui's CRM Call menu): `available`
// false → offer the device path only.
export async function workspaceCallAvailability() {
  await ensureWorkspaceVoice.call(this)
  if (_rt.voice) {
    const accountIds = Array.isArray(_rt.voice.accountIds) ? [..._rt.voice.accountIds] : []
    return { available: true, mode: (_rt.session && _rt.session.mode) || null, accountIds }
  }
  return { available: false, mode: null, code: _refusalCode() }
}

// el.call('startWorkspaceCall', { partyId | conversationId | number | to,
// regarding?, name?, callId? }) → { ok: true, callId } | { ok: false, code }.
//
// `name` is the label the caller already shows (the contact's name) — the
// call row carries no party name. `number` is an alias of `to`. `callId`
// ADOPTS a call that already exists (started elsewhere): the panel follows it
// from the stream; hang-up goes through the server (see hangUpWorkspaceCall).
// A refusal code in NO_BROWSER_CODES (or 'unsupported' / 'unavailable', or
// the role refusal `forbidden_workspace_role`) leaves the panel hidden so the
// caller can fall back to the device path — the same answer for comms-ui's
// 409 use_browser_calling hand-off.
export async function startWorkspaceCall(opts = {}) {
  const { partyId, conversationId, regarding, name, callId, accountId } = opts || {}
  const to = (opts && (opts.to || opts.number)) || undefined
  if (!callId && !partyId && !conversationId && !to) return { ok: false, code: 'bad_request' }
  await ensureWorkspaceVoice.call(this)
  const voice = _rt.voice
  if (!voice) return { ok: false, code: _refusalCode() }
  if (_rt.panel && _rt.panel.phase !== 'ended') return { ok: false, code: 'busy' }
  _clearTimers()
  _detachHandle()
  _rt.incoming = null
  const base = { direction: 'out', status: 'initiated', name: name || null, number: to || null }
  if (callId) {
    // v3 (2): follow a call started elsewhere. The attached handle carries no
    // audio here (no mute / keypad / hold); its hangup() goes through the
    // SDK's calls hang-up (POST /calls/:id/hangup).
    _write({ ...PANEL_SHAPE, ...base, callId })
    let attached
    try {
      attached = await voice.attach(callId)
    } catch (err) {
      if (_rt.voice === voice) _write(null)
      return { ok: false, code: voiceErrorCode(err) || 'not_found' }
    }
    if (_rt.voice !== voice || !attached) return { ok: false, code: 'stopped' }
    _attachHandle(attached)
    if (attached.call) _applyRow(attached.call)
    return { ok: true, callId, adopted: true }
  }
  _write({ ...PANEL_SHAPE, ...base, busy: true })
  let handle
  try {
    handle = await voice.call({
      ...(partyId ? { partyId } : {}),
      ...(conversationId ? { conversationId } : {}),
      ...(to ? { to } : {}),
      ...(regarding ? { regarding } : {}),
      ...(accountId ? { accountId } : {})
    })
  } catch (err) {
    if (_rt.voice !== voice) return { ok: false, code: 'stopped' }
    const code = voiceErrorCode(err) || 'call_not_started'
    if (_callRoleRefused(code)) {
      // The member's role dropped mid-session: no failed panel (the caller
      // falls back to the device path, as for NO_BROWSER_CODES), and the
      // session the server no longer honours ends (THE ROLE GATE).
      _write(null)
      _callEndForRole(this, code)
      return { ok: false, code }
    }
    if (NO_BROWSER_CODES.includes(code)) {
      _write(null)
      return { ok: false, code }
    }
    _write({
      ...(_rt.panel || PANEL_SHAPE),
      ...base,
      status: 'failed',
      phase: 'ended',
      busy: false,
      errorKey: callErrorKey(code)
    })
    _shellSettle()
    return { ok: false, code }
  }
  if (_rt.voice !== voice || !handle) return { ok: false, code: 'stopped' }
  _write({ ...(_rt.panel || PANEL_SHAPE), callId: handle.callId || null, busy: false })
  _attachHandle(handle)
  if (handle.call) _applyRow(handle.call)
  return { ok: true, callId: handle.callId || null }
}

// Render-side reads (the panel component).
export function workspaceCallEyebrow(panel) {
  const key = callEyebrowKey(panel)
  return key ? this.call('polyglot', key) : ''
}

export function workspaceCallTitle(panel) {
  if (!panel) return ''
  return panel.name || panel.number || this.call('polyglot', 'workspace.callPanel.unknownCaller')
}

export function workspaceCallClock(panel) {
  if (!panel) return ''
  if (panel.phase === 'active') return formatCallDuration(callElapsedSeconds(panel))
  if (panel.phase === 'ended' && panel.status === 'completed') {
    return this.call('polyglot', 'workspace.callPanel.duration').replace(
      '{duration}',
      formatCallDuration(callElapsedSeconds(panel))
    )
  }
  return ''
}

// The line under the name: the number when a name is shown, and the clock.
export function workspaceCallMeta(panel) {
  if (!panel) return ''
  const parts = []
  if (panel.name && panel.number) parts.push(panel.number)
  const clock = workspaceCallClock.call(this, panel)
  if (clock) parts.push(clock)
  return parts.join(' · ')
}

export function workspaceCallRingMayTakeFocus(active) {
  return ringMayTakeFocus(active)
}

export function workspaceCallClear(rect) {
  return callClearFor(rect)
}

export function workspaceCallMissed(panel) {
  return isMissedCall(panel)
}

// Test seam: reset the module singleton between cases.
export const __resetWorkspaceCall = () => {
  _callTeardown()
  Object.assign(_rt, {
    root: null,
    el: null,
    panel: null,
    blockedWs: null,
    retryAt: 0,
    refusal: null,
    refusedRole: null,
    unsupported: false
  })
}
